import React, { useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import styled from 'styled-components';
import { gameData } from '../data/levels';
import FullScreenVideoPlayer from '../components/FullScreenVideoPlayer';

const LevelContainer = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 3rem 2rem;
  min-height: 100vh;

  @media (max-width: 768px) {
    padding: 2rem 1rem;
  }

  @media (max-width: 480px) {
    padding: 1rem 0.5rem;
  }
`;

const BackLink = styled(Link)`
  display: inline-flex;
  align-items: center;
  color: #8B4513;
  text-decoration: none;
  margin-bottom: 2rem;
  font-weight: 600;
  padding: 0.75rem 1.5rem;
  border: 2px solid #8B4513;
  border-radius: 30px;
  transition: all 0.3s ease;

  &:hover {
    background: #8B4513;
    color: white;
    transform: translateY(-2px);
    box-shadow: 0 4px 12px rgba(139, 69, 19, 0.3);
  }
`;

const LevelHeader = styled.div`
  background: rgba(255, 255, 255, 0.8);
  backdrop-filter: blur(10px);
  border-radius: 20px;
  padding: 2.5rem;
  margin-bottom: 2rem;
  border: 1px solid rgba(139, 69, 19, 0.1);
  box-shadow: 0 8px 32px rgba(139, 69, 19, 0.15);
`;

const LevelTitle = styled.h1`
  font-size: 2.5rem;
  margin-bottom: 1.5rem;
  color: #2C3E50;
  font-weight: 800;
  text-align: center;

  @media (max-width: 768px) {
    font-size: 2rem;
  }

  @media (max-width: 480px) {
    font-size: 1.5rem;
  }
`;

const LevelInfo = styled.div`
  display: flex;
  gap: 2rem;
  margin-bottom: 1.5rem;
  flex-wrap: wrap;
  justify-content: center;
`;

const LevelInfoItem = styled.div`
  color: #8B4513;
  font-weight: 500;
  padding: 0.5rem 1rem;
  background: rgba(139, 69, 19, 0.1);
  border-radius: 20px;
`;

const LevelDescription = styled.p`
  color: #34495E;
  line-height: 1.6;
  font-size: 1.1rem;
  text-align: center;
  margin-bottom: 1.5rem;
`;

const VideoSection = styled.div`
  background: rgba(255, 255, 255, 0.8);
  backdrop-filter: blur(10px);
  border-radius: 20px;
  padding: 2.5rem;
  border: 1px solid rgba(139, 69, 19, 0.1);
  box-shadow: 0 8px 32px rgba(139, 69, 19, 0.15);
`;

const VideoTitle = styled.h2`
  font-size: 2rem;
  margin-bottom: 1.5rem;
  color: #2C3E50;
  font-weight: 700;
  text-align: center;
`;

const VideoContainer = styled.div`
  position: relative;
  width: 100%;
  padding-bottom: 56.25%; /* 16:9 aspect ratio */
  background: #1a202c;
  border-radius: 12px;
  overflow: hidden;
  cursor: pointer;

  @media (max-width: 768px) {
    padding-bottom: 65%; /* Slightly taller on mobile */
    border-radius: 8px;
  }

  @media (max-width: 480px) {
    padding-bottom: 75%; /* Even taller on small mobile */
    border-radius: 0;
  }
`;

const VideoPlaceholder = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  background: linear-gradient(135deg, #2d3748, #1a202c);
  color: white;
`;

const PlayButton = styled.button`
  width: 80px;
  height: 80px;
  border-radius: 50%;
  background: linear-gradient(135deg, #667eea, #764ba2);
  border: none;
  color: white;
  font-size: 2rem;
  cursor: pointer;
  transition: all 0.3s ease;
  margin-bottom: 1rem;

  &:hover {
    transform: scale(1.1);
    box-shadow: 0 8px 24px rgba(102, 126, 234, 0.4);
  }

  @media (max-width: 768px) {
    width: 70px;
    height: 70px;
    font-size: 1.8rem;
  }

  @media (max-width: 480px) {
    width: 60px;
    height: 60px;
    font-size: 1.5rem;
  }
`;

const VideoText = styled.p`
  font-size: 1.125rem;
  text-align: center;
`;

const DifficultyBadge = styled.span<{ difficulty: string }>`
  padding: 0.5rem 1rem;
  border-radius: 20px;
  font-size: 0.875rem;
  font-weight: 600;
  background: ${props => {
    switch (props.difficulty) {
      case 'easy': return '#48bb78';
      case 'medium': return '#ed8936';
      case 'hard': return '#f56565';
      default: return '#718096';
    }
  }};
  color: white;
`;

const TagsContainer = styled.div`
  display: flex;
  gap: 0.5rem;
  flex-wrap: wrap;
  margin-top: 1rem;
`;

const Tag = styled.span`
  padding: 0.25rem 0.75rem;
  background: rgba(255, 255, 255, 0.2);
  border-radius: 15px;
  color: rgba(255, 255, 255, 0.9);
  font-size: 0.875rem;
`;

const LevelDetail: React.FC = () => {
  const { levelId } = useParams<{ levelId: string }>();
  const [isFullScreen, setIsFullScreen] = useState(false);

  // Find the level from gameData
  let level = null;
  for (const chapter of gameData) {
    const foundLevel = chapter.levels.find(l => l.id === levelId);
    if (foundLevel) {
      level = foundLevel;
      break;
    }
  }

  const handleFullScreen = () => {
    setIsFullScreen(true);
  };

  const handleCloseFullScreen = () => {
    setIsFullScreen(false);
  };

  if (!level) {
    return (
      <LevelContainer>
        <BackLink to="/chapters">← 返回章节选择</BackLink>
        <div style={{ color: 'white', textAlign: 'center', padding: '2rem' }}>
          <h2>关卡未找到</h2>
          <p>请返回章节选择页面</p>
        </div>
      </LevelContainer>
    );
  }

  return (
    <>
      <LevelContainer>
        <BackLink to="/chapters">← 返回章节选择</BackLink>

        <LevelHeader>
          <LevelTitle>{level.name}</LevelTitle>
          <LevelInfo>
            <LevelInfoItem>
              第{level.chapterNumber}章 - {level.chapter}
            </LevelInfoItem>
            <LevelInfoItem>
              关卡 {level.levelNumber}
            </LevelInfoItem>
            <LevelInfoItem>
              <DifficultyBadge difficulty={level.difficulty}>
                {level.difficulty === 'easy' ? '简单' :
                 level.difficulty === 'medium' ? '中等' : '困难'}
              </DifficultyBadge>
            </LevelInfoItem>
          </LevelInfo>
          <LevelDescription>{level.description}</LevelDescription>
          <TagsContainer>
            {level.tags.map((tag, index) => (
              <Tag key={index}>{tag}</Tag>
            ))}
          </TagsContainer>
        </LevelHeader>

        <VideoSection>
          <VideoTitle>通关视频</VideoTitle>
          <VideoContainer>
            <VideoPlaceholder>
              <PlayButton onClick={handleFullScreen}>
                ▶
              </PlayButton>
              <VideoText>点击播放通关视频</VideoText>
            </VideoPlaceholder>
          </VideoContainer>
        </VideoSection>
      </LevelContainer>

      {/* 使用全屏视频播放器组件 */}
      {level.videoUrl && (
        <FullScreenVideoPlayer
          videoUrl={level.videoUrl!}
          isVisible={isFullScreen}
          onClose={handleCloseFullScreen}
          autoPlay={true}
        />
      )}
    </>
  );
};

export default LevelDetail;